import { ClickAwayListener } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import removeIcon from "../../images/remove.svg";
import editIcon from "../../images/edit-pen.svg";
import { createPortal } from "react-dom";

interface Props {
  visible: boolean;
  onClose: () => unknown;
  onRemove?: () => unknown;
  onEdit?: () => unknown;
  parentRef: React.RefObject<HTMLDivElement>;
}

interface optionAction {
  imageSource: string;
  text: string;
  isVisible: boolean;
  onClick: () => unknown;
  color: string;
  hoverColor: string;
  hoverBgColor: string;
}

export const MoreOptions = ({
  visible,
  onClose,
  onRemove,
  onEdit,
  parentRef,
}: Props) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      if (parentRef.current) {
        const rect = parentRef.current.getBoundingClientRect();
        // Position to the left of the icon with proper spacing
        const x = rect.left - 140; // 120px width + 20px spacing
        const y = rect.top + rect.height / 2 - 50; // Center vertically with menu
        setPosition({ x, y });
      }
    };

    // Update position immediately
    updatePosition();

    // Update position on scroll
    const handleScroll = () => {
      updatePosition();
    };

    // Update position on window resize
    const handleResize = () => {
      updatePosition();
    };

    // Listen to scroll on window and all parent scroll containers
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    // Use requestAnimationFrame for smooth updates
    let rafId: number;
    const smoothUpdate = () => {
      updatePosition();
      rafId = requestAnimationFrame(smoothUpdate);
    };
    rafId = requestAnimationFrame(smoothUpdate);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [visible, parentRef]);

  if (!visible) return <></>;

  const optionsAction: optionAction[] = [
    {
      imageSource: editIcon,
      text: "Edit",
      isVisible: !!onEdit,
      onClick: !!onEdit ? onEdit : () => {},
      color: Colors.TangerineOrange,
      hoverColor: Colors.TangerineOrange,
      hoverBgColor: Colors.LIGHT_TANGERINE_ORANGE,
    },
    {
      imageSource: removeIcon,
      text: "Remove",
      isVisible: !!onRemove,
      onClick: !!onRemove ? onRemove : () => {},
      color: Colors.Tomato,
      hoverColor: Colors.Tomato,
      hoverBgColor: "rgba(255, 0, 0, 0.08)",
    },
  ];

  const visibleOptions = optionsAction.filter((action) => action.isVisible);

  return createPortal(
    <ClickAwayListener
      onClickAway={() => {
        onClose();
      }}
    >
      <div
        ref={divRef}
        style={{
          position: "fixed",
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 9999,
          animation: "slideInLeft 0.15s ease-out",
        }}
      >
        <style>
          {`
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(10px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
          `}
        </style>
        <div
          className="flex flex-col"
          style={{
            padding: "8px",
            borderRadius: "8px",
            width: "140px",
            backgroundColor: Colors.White,
            boxShadow:
              "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${Colors.LightAshRose}`,
            gap: "4px",
          }}
        >
          {visibleOptions.map((action: optionAction, index: number) => {
            const isHovered = hoveredIndex === index;
            return (
              <div
                key={action.text}
                className="flex flex-row items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                  onClose();
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor: isHovered
                    ? action.hoverBgColor
                    : "transparent",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.2s ease",
                    transform: isHovered ? "scale(1.1)" : "scale(1)",
                  }}
                >
                  <img
                    src={action.imageSource}
                    alt={`${action.text}-icon`}
                    style={{
                      width: "100%",
                      height: "100%",
                      filter: isHovered ? "brightness(0.9)" : "brightness(1)",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: isHovered ? "500" : "400",
                    color: isHovered ? action.hoverColor : Colors.DarkGrey,
                    fontFamily: FontFamily.POPPINS_REGULAR,
                    transition: "all 0.2s ease",
                  }}
                >
                  {action.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ClickAwayListener>,
    document.body,
  );
};
